import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  QuestionAnswer as QuestionIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { questionBasesApi, fileSourcesApi } from '../services/api';

function QuestionBases() {
  const navigate = useNavigate();
  const [bases, setBases] = useState([]);
  const [fileSources, setFileSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', file_source_ids: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const basesRes = await questionBasesApi.list();
      setBases(basesRes.data);
    } catch (error) {
      console.error('Error loading question bases:', error);
    }
    try {
      const sourcesRes = await fileSourcesApi.list();
      setFileSources(sourcesRes.data);
    } catch (error) {
      console.error('Error loading file sources:', error);
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) return;
    
    setSaving(true);
    try {
      await questionBasesApi.create(formData);
      setDialogOpen(false);
      setFormData({ name: '', description: '', file_source_ids: [] });
      loadData();
    } catch (error) {
      console.error('Error creating question base:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir esta base de perguntas?')) return;
    
    try {
      await questionBasesApi.delete(id);
      loadData();
    } catch (error) {
      console.error('Error deleting question base:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Bases de Perguntas</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Nova Base
        </Button>
      </Box>

      {bases.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <QuestionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Nenhuma base de perguntas cadastrada
              </Typography>
              <Typography color="textSecondary" mb={2}>
                Crie uma base de perguntas para gerar perguntas sintéticas a partir das fontes de dados
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
              >
                Criar Primeira Base
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {bases.map((base) => (
            <Grid item xs={12} sm={6} md={4} key={base.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={() => navigate(`/question-bases/${base.id}`)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <QuestionIcon color="primary" />
                    <Typography variant="h6" noWrap>
                      {base.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2,
                    }}
                  >
                    {base.description || 'Sem descrição'}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
                    <Chip
                      icon={<QuestionIcon />}
                      label={`${base.questions_count || 0} perguntas`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </Box>
                  {base.file_sources && base.file_sources.length > 0 && (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {base.file_sources.map((fs) => (
                        <Chip
                          key={fs.id}
                          icon={<FolderIcon />}
                          label={fs.name}
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => navigate(`/question-bases/${base.id}`)}>
                    Ver Detalhes
                  </Button>
                  <Box flexGrow={1} />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => handleDelete(base.id, e)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Base de Perguntas</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Descrição"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Fontes de Dados</InputLabel>
            <Select
              multiple
              value={formData.file_source_ids}
              onChange={(e) => setFormData({ ...formData, file_source_ids: e.target.value })}
              input={<OutlinedInput label="Fontes de Dados" />}
              renderValue={(selected) =>
                selected
                  .map((id) => {
                    const fs = fileSources.find((s) => s.id === id);
                    return fs ? fs.name : id;
                  })
                  .join(', ')
              }
            >
              {fileSources.map((source) => (
                <MenuItem key={source.id} value={source.id}>
                  <Checkbox checked={formData.file_source_ids.indexOf(source.id) > -1} />
                  <ListItemText
                    primary={source.name}
                    secondary={`${source.files?.length || 0} arquivos`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {fileSources.length === 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Nenhuma fonte de dados disponível. Crie uma fonte de dados primeiro.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!formData.name.trim() || saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default QuestionBases;
